<template>
    <Transition name="modal-fade">
        <div v-if="isVisible" class="cf-overlay" role="dialog" aria-modal="true" @click.self="close">
            <Transition name="modal-slide">
                <div v-if="isVisible" class="cf-dialog cf-dialog--trf" @click.stop>

                    <!-- ========== HEADER ========== -->
                    <div class="cf-modal-head cf-dialog-head">
                        <div class="cf-modal-head__main">
                            <span class="cf-modal-head__icon"><i class="ri-exchange-line"></i></span>
                            <div>
                                <h4 class="cf-dialog-title">Transformation de document</h4>
                                <p class="cf-modal-head__sub">Sélectionnez le nouveau type de document</p>
                            </div>
                        </div>
                        <button @click="close" type="button" class="cf-dialog-close" aria-label="Fermer"><i class="ri-close-line"></i></button>
                    </div>

                    <!-- ========== BODY ========== -->
                    <div class="cf-dialog-body">

                        <!-- ========== DOCUMENT INFO ========== -->
                        <div class="trf-doc">
                            <span class="trf-doc__icon"><i class="ri-file-transfer-line"></i></span>
                            <div class="trf-doc__main">
                                <p class="trf-doc__label">Document à transformer</p>
                                <div class="flex items-baseline gap-2 mt-1">
                                    <span class="trf-doc__num">{{ documentData.numdoc }}</span>
                                    <span :class="['trf-doc__badge', getDocumentTypeClass(documentType)]">
                                        {{ getDocumentTypeLabel(documentType) }}
                                    </span>
                                </div>
                                <p v-if="documentData.nameclient" class="trf-doc__client">
                                    {{ documentData.nameclient }}
                                </p>
                            </div>
                        </div>

                        <!-- ========== TRANSFORMATION OPTIONS ========== -->
                        <!-- Process Indicator -->
                        <div class="trf-listhead">
                            <p class="trf-listhead__title">Transformations disponibles</p>
                            <div class="trf-online">
                                <span class="trf-online__dot"></span>
                                <span>En ligne</span>
                            </div>
                        </div>

                        <!-- Transformation Steps -->
                        <div class="trf-list">
                            <!-- Achat Flow -->
                            <template v-if="documentCategory === 'achat'">
                                <!-- DAF → BCF -->
                                <transition-group name="list" tag="div" class="trf-list__inner">
                                    <div v-if="documentType === 'DAF' && canTransformTo('BCF')" key="daf-bcf">
                                        <button @click="handleTransformation('BCF')" type="button" class="trf-option">
                                            <span class="trf-option__icon trf-option__icon--info"><i class="ri-checkbox-circle-line"></i></span>
                                            <span class="trf-option__body">
                                                <span class="trf-option__title">Bon de Commande Fournisseur</span>
                                                <span class="trf-option__sub">Transformer en bon de commande officiel</span>
                                            </span>
                                            <i class="ri-arrow-right-line trf-option__arrow"></i>
                                        </button>
                                    </div>

                                    <!-- DAF/BCF → BRF -->
                                    <div v-if="['DAF', 'BCF'].includes(documentType) && canTransformTo('BRF')" key="to-brf">
                                        <button @click="handleTransformation('BRF')" type="button" class="trf-option">
                                            <span class="trf-option__icon trf-option__icon--success"><i class="ri-check-line"></i></span>
                                            <span class="trf-option__body">
                                                <span class="trf-option__title">Bon de Réception Fournisseur</span>
                                                <span class="trf-option__sub">Confirmer la réception des marchandises</span>
                                            </span>
                                            <i class="ri-arrow-right-line trf-option__arrow"></i>
                                        </button>
                                    </div>

                                    <!-- DAF/BCF/BRF → FR -->
                                    <div v-if="['DAF', 'BCF', 'BRF'].includes(documentType) && canTransformTo('FR')" key="to-fr">
                                        <button @click="handleTransformation('FR')" type="button" class="trf-option">
                                            <span class="trf-option__icon trf-option__icon--primary"><i class="ri-file-text-line"></i></span>
                                            <span class="trf-option__body">
                                                <span class="trf-option__title">Facture Fournisseur</span>
                                                <span class="trf-option__sub">Générer la facture définitive</span>
                                            </span>
                                            <i class="ri-arrow-right-line trf-option__arrow"></i>
                                        </button>
                                    </div>

                                    <!-- BRF/FR → RF -->
                                    <div v-if="['BRF', 'FR'].includes(documentType) && canTransformTo('RF')" key="to-rf">
                                        <button @click="handleTransformation('RF')" type="button" class="trf-option">
                                            <span class="trf-option__icon trf-option__icon--warning"><i class="ri-refresh-line"></i></span>
                                            <span class="trf-option__body">
                                                <span class="trf-option__title">Retour sur Achat</span>
                                                <span class="trf-option__sub">Gérer les retours de marchandises</span>
                                            </span>
                                            <i class="ri-arrow-right-line trf-option__arrow"></i>
                                        </button>
                                    </div>
                                </transition-group>
                            </template>

                            <!-- Vente Flow -->
                            <template v-else-if="documentCategory === 'vente'">
                                <!-- PF → BC/BPL/BL/F -->
                                <transition-group name="list" tag="div" class="trf-list__inner">
                                    <div v-if="documentType === 'PF' && canTransformTo('BC')" key="pf-bc">
                                        <button @click="handleTransformation('BC')" type="button" class="trf-option">
                                            <span class="trf-option__icon trf-option__icon--info"><i class="ri-checkbox-circle-line"></i></span>
                                            <span class="trf-option__body">
                                                <span class="trf-option__title">Bon de Commande Client</span>
                                                <span class="trf-option__sub">Confirmer la commande client</span>
                                            </span>
                                            <i class="ri-arrow-right-line trf-option__arrow"></i>
                                        </button>
                                    </div>

                                    <div v-if="['PF', 'BC'].includes(documentType) && canTransformTo('BPL')" key="pf-bpl">
                                        <button @click="handleTransformation('BPL')" type="button" class="trf-option">
                                            <span class="trf-option__icon trf-option__icon--success"><i class="ri-check-line"></i></span>
                                            <span class="trf-option__body">
                                                <span class="trf-option__title">Préparation de Livraison</span>
                                                <span class="trf-option__sub">Préparer les articles pour la livraison</span>
                                            </span>
                                            <i class="ri-arrow-right-line trf-option__arrow"></i>
                                        </button>
                                    </div>

                                    <div v-if="['PF', 'BC', 'BPL'].includes(documentType) && canTransformTo('BL')" key="to-bl">
                                        <button @click="handleTransformation('BL')" type="button" class="trf-option">
                                            <span class="trf-option__icon trf-option__icon--secondary"><i class="ri-truck-line"></i></span>
                                            <span class="trf-option__body">
                                                <span class="trf-option__title">Bon de Livraison</span>
                                                <span class="trf-option__sub">Livrer les marchandises au client</span>
                                            </span>
                                            <i class="ri-arrow-right-line trf-option__arrow"></i>
                                        </button>
                                    </div>

                                    <div v-if="['PF', 'BC', 'BPL', 'BL'].includes(documentType) && canTransformTo('F')" key="to-f">
                                        <button @click="handleTransformation('F')" type="button" class="trf-option">
                                            <span class="trf-option__icon trf-option__icon--primary"><i class="ri-file-text-line"></i></span>
                                            <span class="trf-option__body">
                                                <span class="trf-option__title">Facture Client</span>
                                                <span class="trf-option__sub">Générer la facture définitive</span>
                                            </span>
                                            <i class="ri-arrow-right-line trf-option__arrow"></i>
                                        </button>
                                    </div>

                                    <div v-if="['BL', 'F'].includes(documentType) && canTransformTo('BR')" key="to-br">
                                        <button @click="handleTransformation('BR')" type="button" class="trf-option">
                                            <span class="trf-option__icon trf-option__icon--warning"><i class="ri-refresh-line"></i></span>
                                            <span class="trf-option__body">
                                                <span class="trf-option__title">Retour sur Vente</span>
                                                <span class="trf-option__sub">Gérer les retours clients</span>
                                            </span>
                                            <i class="ri-arrow-right-line trf-option__arrow"></i>
                                        </button>
                                    </div>

                                    <div v-if="documentType === 'BR' && canTransformTo('FR')" key="br-fr">
                                        <button @click="handleTransformation('FR')" type="button" class="trf-option">
                                            <span class="trf-option__icon trf-option__icon--danger"><i class="ri-file-reduce-line"></i></span>
                                            <span class="trf-option__body">
                                                <span class="trf-option__title">Facture d'Avoir</span>
                                                <span class="trf-option__sub">Établir la facture d'avoir</span>
                                            </span>
                                            <i class="ri-arrow-right-line trf-option__arrow"></i>
                                        </button>
                                    </div>
                                </transition-group>
                            </template>

                            <!-- No Transformations Available -->
                            <div v-if="availableTransformations.length === 0" class="trf-empty">
                                <span class="trf-empty__icon"><i class="ri-error-warning-line"></i></span>
                                <p class="trf-empty__title">Aucune transformation disponible</p>
                                <p class="trf-empty__sub">Ce document ne peut pas être transformé davantage</p>
                            </div>
                        </div>
                    </div>

                    <!-- ========== FOOTER ========== -->
                    <div class="cf-dialog-foot cf-dialog-foot--split">
                        <span class="trf-count">
                            <span class="trf-count__num">{{ availableTransformations.length }}</span>
                            transformation{{ availableTransformations.length > 1 ? 's' : '' }} disponible{{ availableTransformations.length > 1 ? 's' : '' }}
                        </span>
                        <button @click="close" type="button" class="ti-btn ti-btn-secondary-full">Fermer</button>
                    </div>

                    <!-- Loading Overlay -->
                    <transition name="fade">
                        <div v-if="isLoading" class="trf-loading">
                            <div class="text-center">
                                <div class="relative w-16 h-16 mx-auto">
                                    <!-- Cercle de fond blanc -->
                                    <div class="absolute inset-0 border-4 border-white rounded-full"></div>

                                    <!-- Anneau noir en rotation -->
                                    <div class="absolute inset-0 border-4 border-t-transparent rounded-full animate-spin"></div>
                                </div>
                                <p class="text-lg font-medium text-white">Transformation en cours...</p>
                                <p class="text-sm text-white mt-1">Création du nouveau document</p>
                            </div>
                        </div>
                    </transition>

                </div>
            </Transition>
        </div>
    </Transition>
</template>

<script>
    import {
        achatscommande,
        achatsdemande,
        achatsfacture,
        achatsreception,
        achatsretour,
        ventesavoir,
        ventescommande,
        ventesdevis,
        ventesfacture,
        venteslivraison,
        ventespreparation,
        ventesretour
    } from "@/service/directives/vpermission";

    const axios = require('axios');
    import { randomString } from '@/allfunction';
    import * as JwtService from '@/common/jwt.service';
    import permissionHelper from "@/service/directives/permissionHelper";

    export default {
        /* eslint-disable */
        name: 'DocumentTransformationModal',

        data() {
            return {
                isVisible: false,
                isLoading: false,
                titleModal: '',
                documentData: {},
                documentType: '',
                originalType: '',
                documentCategory: '',
                transformationResult: null,
            };
        },

        computed: {
            availableTransformations() {
                const transformations = [];

                if (this.documentCategory === 'achat') {
                    if (this.documentType === 'DAF') {
                        if (this.canTransformTo('BCF')) transformations.push({ type: 'BCF', label: 'Bon de Commande', description: 'Transformer en bon de commande officiel', color: 'blue' });
                        if (this.canTransformTo('BRF')) transformations.push({ type: 'BRF', label: 'Bon de Réception', description: 'Confirmer la réception des marchandises', color: 'green' });
                        if (this.canTransformTo('FR')) transformations.push({ type: 'FR', label: 'Facture', description: 'Générer la facture définitive', color: 'purple' });
                    }
                    if (this.documentType === 'BCF') {
                        if (this.canTransformTo('BRF')) transformations.push({ type: 'BRF', label: 'Bon de Réception', description: 'Confirmer la réception des marchandises', color: 'green' });
                        if (this.canTransformTo('FR')) transformations.push({ type: 'FR', label: 'Facture', description: 'Générer la facture définitive', color: 'purple' });
                    }
                    if (this.documentType === 'BRF') {
                        if (this.canTransformTo('FR')) transformations.push({ type: 'FR', label: 'Facture', description: 'Générer la facture définitive', color: 'purple' });
                        if (this.canTransformTo('RF')) transformations.push({ type: 'RF', label: 'Retour sur Achat', description: 'Gérer les retours de marchandises', color: 'amber' });
                    }
                    if (this.documentType === 'FR') {
                        if (this.canTransformTo('RF')) transformations.push({ type: 'RF', label: 'Retour sur Achat', description: 'Gérer les retours de marchandises', color: 'amber' });
                    }
                } else if (this.documentCategory === 'vente') {
                    if (this.documentType === 'PF') {
                        if (this.canTransformTo('BC')) transformations.push({ type: 'BC', label: 'Bon de Commande', description: 'Confirmer la commande client', color: 'blue' });
                        if (this.canTransformTo('BPL')) transformations.push({ type: 'BPL', label: 'Préparation Livraison', description: 'Préparer les articles pour la livraison', color: 'green' });
                        if (this.canTransformTo('BL')) transformations.push({ type: 'BL', label: 'Bon de Livraison', description: 'Livrer les marchandises au client', color: 'indigo' });
                        if (this.canTransformTo('F')) transformations.push({ type: 'F', label: 'Facture Client', description: 'Générer la facture définitive', color: 'purple' });
                    }
                    if (this.documentType === 'BC') {
                        if (this.canTransformTo('BPL')) transformations.push({ type: 'BPL', label: 'Préparation Livraison', description: 'Préparer les articles pour la livraison', color: 'green' });
                        if (this.canTransformTo('BL')) transformations.push({ type: 'BL', label: 'Bon de Livraison', description: 'Livrer les marchandises au client', color: 'indigo' });
                        if (this.canTransformTo('F')) transformations.push({ type: 'F', label: 'Facture Client', description: 'Générer la facture définitive', color: 'purple' });
                    }
                    if (this.documentType === 'BPL') {
                        if (this.canTransformTo('BL')) transformations.push({ type: 'BL', label: 'Bon de Livraison', description: 'Livrer les marchandises au client', color: 'indigo' });
                        if (this.canTransformTo('F')) transformations.push({ type: 'F', label: 'Facture Client', description: 'Générer la facture définitive', color: 'purple' });
                    }
                    if (this.documentType === 'BL') {
                        if (this.canTransformTo('F')) transformations.push({ type: 'F', label: 'Facture Client', description: 'Générer la facture définitive', color: 'purple' });
                        if (this.canTransformTo('BR')) transformations.push({ type: 'BR', label: 'Retour sur Vente', description: 'Gérer les retours clients', color: 'amber' });
                    }
                    if (this.documentType === 'F') {
                        if (this.canTransformTo('BR')) transformations.push({ type: 'BR', label: 'Retour sur Vente', description: 'Gérer les retours clients', color: 'amber' });
                    }
                    if (this.documentType === 'BR') {
                        if (this.canTransformTo('FA')) transformations.push({ type: 'FA', label: 'Facture d\'Avoir', description: 'Établir la facture d\'avoir', color: 'red' });
                    }
                }

                return transformations;
            },
        },

        methods: {
            show(title, documentData, documentType, documentCategory) {
                this.titleModal = title;
                this.documentData = documentData;
                this.documentType = documentType;
                this.originalType = documentType;
                this.documentCategory = documentCategory;
                this.transformationResult = null;
                this.isVisible = true;
                document.body.style.overflow = 'hidden';
            },

            close() {
                if (!this.isLoading) {
                    this.isVisible = false;
                    document.body.style.overflow = '';
                    this.emitSelection('0');
                }
            },

            canTransformTo(targetType) {
               /* const permissionMap = {
                    // Achat
                    'BCF': 'cccommercialachatbondecommande',
                    'BRF': 'cccommercialachatbondereception',
                    'FR': 'cccommercialachatfacture',
                    'RF': 'cccommercialachatretoursurachat',
                    // Vente
                    'BC': 'cccommercialventebondecommande',
                    'BPL': 'cccommercialventebondepreparationlivraison',
                    'BL': 'cccommercialventebondelivraison',
                    'F': 'cccommercialventefacture',
                    'BR': 'cccommercialventeretoursurvente',
                    'FR': 'cccommercialventefacture', // Facture d'avoir
                };

                const permKey = permissionMap[targetType];
                return this.$store.state.accescomponent[permKey] === true;*/

                const userPermissions = this.$store.state.currentuserpermissions;


                let canTransform=false;
                const permissionMap = {
                    // Achat
                    'DAF':achatsdemande ,
                    'BCF':achatscommande ,
                    'BRF':achatsreception ,
                    'FR':achatsfacture ,
                    'RF':achatsretour ,
                    // Vente
                    'PF': ventesdevis,
                    'BC': ventescommande,
                    'BPL':ventespreparation ,
                    'BL': venteslivraison,
                    'F': ventesfacture,
                    'BR': ventesretour,
                    'FA':ventesavoir , // Facture d'avoir
                };
                const config = permissionMap[targetType];
                canTransform = permissionHelper.check(config, userPermissions);

                return canTransform;

            },

            async handleTransformation(newType) {
                this.isLoading = true;

                try {
                    const url = `${process.env.VUE_APP_API_URL}/jkebfgescomporziprodpfachatventeegl745874stock771714dfe/gescom/default/valtransadd/${newType}/${this.originalType}/${this.documentData.pid}/${this.documentCategory}/${this.documentType}/${this.$store.state.appsociete}/${randomString()}`;

                    const response = await axios.post(url, this.documentData, {
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            'Accept': 'application/json',
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${JwtService.getToken()}`,
                            'csrftoken': this.$store.state.appsociete
                        },
                        withCredentials: true
                    });

                    if (response.data.success) {
                        // Show success notification

                        toastr['success']( response.data.message || 'Transformation réussie !',"<b></b>");

                        // Emit success result
                        this.transformationResult = {
                            type: '1',
                            numdoc: response.data.numdoc,
                            keypid: response.data.pid,
                            mynewtypdoc: newType,
                            documentData: response.data.documentData
                        };

                        this.emitSelection('1');
                        this.isVisible = false;
                    } else {
                        throw new Error(response.data.message || 'Erreur lors de la transformation');
                    }
                } catch (error) {
                    console.error('Transformation error:', error);

                    toastr['error']( error.message || 'Erreur lors de la transformation',"<b></b>");
                    this.emitSelection('0');
                } finally {
                    this.isLoading = false;
                }
            },

            emitSelection(value) {
                const selection = {
                    typ: value,
                    numdoc: this.transformationResult?.numdoc || this.documentData.numdoc,
                    keypid: this.transformationResult?.keypid || this.documentData.pid,
                    mynewtypdoc: this.transformationResult?.mynewtypdoc || this.documentType,
                    documentData: this.transformationResult?.documentData || this.documentData
                };

                this.$emit('elementSelectionnetransformation', selection);
            },

            getDocumentTypeClass(type) {
                const typeClasses = {
                    'DAF': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
                    'BCF': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
                    'BRF': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
                    'FR': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
                    'RF': 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
                    'PF': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
                    'BC': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
                    'BPL': 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
                    'BL': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
                    'F': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
                    'BR': 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
                };

                return typeClasses[type] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
            },

            getDocumentTypeLabel(type) {

                const typeLabels = {
                    'DAF': 'Demande Achat',
                    'BCF': 'Bon Commande',
                    'BRF': 'Bon Réception',
                    'FR': 'Facture',
                    'RF': 'Retour Achat',
                    'PF': 'Devis',
                    'BC': 'Bon Commande',
                    'BPL': 'Préparation de livraison',
                    'BL': 'Livraison',
                    'F': 'Facture',
                    'BR': 'Retour Vente',
                    'FA': 'Facture avoir',
                };

                return typeLabels[type] || type;
            },
        },

        beforeUnmount() {
            document.body.style.overflow = '';
        },
    };
</script>

<style scoped>
/* Charte cf-/ds- : tokens uniquement (light-only) */
.cf-overlay { position:fixed; inset:0; z-index:1000; display:flex; align-items:center; justify-content:center; padding:1rem; background:rgb(15 23 42 / 0.45); }
.cf-dialog { position:relative; width:100%; background:#fff; border-radius:var(--cf-radius); box-shadow:var(--cf-shadow-md); display:flex; flex-direction:column; max-height:90vh; overflow:hidden; }
.cf-dialog--trf { max-width:550px; }
.cf-dialog-head { display:flex; align-items:center; justify-content:space-between; padding:1rem 1.25rem; border-bottom:1px solid var(--cf-border); flex-shrink:0; }
.cf-dialog-title { margin:0; font-size:0.95rem; font-weight:700; color:var(--cf-ink-strong); }
.cf-dialog-close { height:2rem; width:2rem; flex-shrink:0; display:inline-flex; align-items:center; justify-content:center; border-radius:var(--cf-radius-sm); border:none; background:var(--cf-surface-alt); color:var(--cf-muted); cursor:pointer; transition:all .15s; }
.cf-dialog-close:hover { background:#fee2e2; color:rgb(var(--danger)); }
.cf-dialog-body { padding:1.1rem 1.25rem; overflow-y:auto; flex:1; }
.cf-dialog-foot { display:flex; align-items:center; justify-content:flex-end; gap:0.6rem; padding:0.9rem 1.25rem; border-top:1px solid var(--cf-border); background:var(--cf-surface); flex-shrink:0; }
.cf-dialog-foot--split { justify-content:space-between; }

/* Bloc document à transformer */
.trf-doc { display:flex; align-items:center; gap:0.9rem; padding:0.85rem 1rem; margin-bottom:1rem; background:var(--cf-surface); border:1px solid var(--cf-border); border-radius:var(--cf-radius); }
.trf-doc__icon { flex-shrink:0; width:42px; height:42px; display:inline-flex; align-items:center; justify-content:center; border-radius:var(--cf-radius-sm); background:rgb(var(--primary) / 0.10); color:rgb(var(--primary)); font-size:1.3rem; }
.trf-doc__main { min-width:0; flex:1; }
.trf-doc__label { margin:0; font-size:0.72rem; text-transform:uppercase; letter-spacing:0.03em; font-weight:600; color:var(--cf-muted); }
.trf-doc__num { font-size:1.05rem; font-weight:700; color:var(--cf-ink-strong); }
.trf-doc__badge { padding:0.15rem 0.55rem; border-radius:var(--cf-radius-xs); font-size:0.68rem; font-weight:700; text-transform:uppercase; letter-spacing:0.03em; }
.trf-doc__client { margin:2px 0 0; font-size:0.82rem; color:var(--cf-ink); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

/* En-tête de liste + indicateur en ligne */
.trf-listhead { display:flex; align-items:center; justify-content:space-between; margin-bottom:0.7rem; }
.trf-listhead__title { margin:0; font-size:0.82rem; font-weight:600; color:var(--cf-ink); }
.trf-online { display:flex; align-items:center; gap:6px; font-size:0.72rem; font-weight:600; color:rgb(var(--success)); }
.trf-online__dot { width:8px; height:8px; border-radius:50%; background:rgb(var(--success)); animation:trf-pulse 1.6s ease-in-out infinite; }
@keyframes trf-pulse { 0%,100% { opacity:1; } 50% { opacity:0.35; } }

/* Liste d'options */
.trf-list { max-height:300px; overflow-y:auto; }
.trf-list__inner { display:flex; flex-direction:column; gap:0.6rem; }

/* Carte d'option de transformation */
.trf-option { width:100%; display:flex; align-items:center; gap:0.85rem; padding:0.8rem 0.9rem; background:#fff; border:1px solid var(--cf-border); border-radius:var(--cf-radius); cursor:pointer; text-align:left; transition:border-color .15s, box-shadow .15s, background-color .15s; }
.trf-option:hover { border-color:rgb(var(--primary) / 0.45); box-shadow:0 0 0 3px rgb(var(--primary) / 0.10); background:rgb(var(--primary) / 0.03); }
.trf-option:focus-visible { outline:none; border-color:rgb(var(--primary)); box-shadow:0 0 0 3px rgb(var(--primary) / 0.10); }
.trf-option__icon { flex-shrink:0; width:44px; height:44px; display:inline-flex; align-items:center; justify-content:center; border-radius:var(--cf-radius-sm); font-size:1.35rem; transition:transform .15s; }
.trf-option:hover .trf-option__icon { transform:scale(1.08); }
.trf-option__icon--info { background:rgb(var(--info) / 0.12); color:rgb(var(--info)); }
.trf-option__icon--success { background:rgb(var(--success) / 0.12); color:rgb(var(--success)); }
.trf-option__icon--primary { background:rgb(var(--primary) / 0.10); color:rgb(var(--primary)); }
.trf-option__icon--secondary { background:rgb(var(--secondary) / 0.12); color:rgb(var(--secondary)); }
.trf-option__icon--warning { background:rgb(var(--warning) / 0.15); color:rgb(var(--warning)); }
.trf-option__icon--danger { background:rgb(var(--danger) / 0.10); color:rgb(var(--danger)); }
.trf-option__body { flex:1; min-width:0; display:flex; flex-direction:column; }
.trf-option__title { font-size:0.88rem; font-weight:600; color:var(--cf-ink-strong); transition:color .15s; }
.trf-option:hover .trf-option__title { color:rgb(var(--primary)); }
.trf-option__sub { margin-top:2px; font-size:0.78rem; color:var(--cf-muted); }
.trf-option__arrow { flex-shrink:0; font-size:1.1rem; color:var(--cf-faint); transition:color .15s, transform .15s; }
.trf-option:hover .trf-option__arrow { color:rgb(var(--primary)); transform:translateX(3px); }

/* État vide */
.trf-empty { text-align:center; padding:2rem 1rem; }
.trf-empty__icon { display:inline-flex; align-items:center; justify-content:center; width:64px; height:64px; margin-bottom:0.9rem; border-radius:50%; background:var(--cf-surface-alt); color:var(--cf-faint); font-size:2rem; }
.trf-empty__title { margin:0; font-size:0.95rem; font-weight:600; color:var(--cf-muted); }
.trf-empty__sub { margin:4px 0 0; font-size:0.8rem; color:var(--cf-faint); }

/* Compteur du pied */
.trf-count { font-size:0.8rem; color:var(--cf-muted); }
.trf-count__num { font-weight:700; color:var(--cf-ink-strong); }

/* Overlay de chargement */
.trf-loading { position:absolute; inset:0; z-index:50; display:flex; align-items:center; justify-content:center; background:rgb(0 0 0 / 0.5); backdrop-filter:blur(2px); border-radius:var(--cf-radius); }

/* Transitions */
.modal-fade-enter-active, .modal-fade-leave-active { transition:opacity 0.25s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity:0; }
.modal-slide-enter-active, .modal-slide-leave-active { transition:all 0.25s ease; }
.modal-slide-enter-from, .modal-slide-leave-to { opacity:0; transform:translateY(-16px); }

.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

.list-enter-active, .list-leave-active { transition: all 0.3s ease; }
.list-enter-from, .list-leave-to { opacity: 0; transform: translateX(30px); }

/* Mobile tap highlight */
@media (hover: none) {
    .trf-option:active { opacity: 0.8; }
}

@media screen and (max-width: 768px) {
    .cf-dialog--trf { max-width:100%; }
    .trf-list { max-height:340px; }
}
</style>
